import {
  clampDuration,
  getClipLimitForModel,
  MAX_DURATION_SEC,
  snapClipDuration,
} from "./durationOptions";
import {
  distributeSegmentDurations,
  extractDialogueFromPrompt,
  extractDialogueLinesFromPrompt,
  needsAutoStitch,
  splitPromptAcrossSegments,
} from "./klingSegmentPlan";

function clipOpts(form) {
  return {
    hailuoResolution: form?.hailuoResolution,
    resolution: form?.resolution,
  };
}

/** Stable compare for scene arrays (duration + count + ids). */
export function scenesStructureKey(scenes) {
  if (!scenes?.length) return "";
  return scenes.map((s) => `${s.id || ""}:${Number(s.duration) || 0}`).join("|");
}

/** Sum of scene lengths in seconds. */
export function totalScenesDuration(scenes) {
  if (!Array.isArray(scenes) || !scenes.length) return 0;
  return scenes.reduce((sum, s) => sum + (Number(s.duration) || 0), 0);
}

/** Rewrite ids to scene-1 … scene-N so numbering stays sequential. */
export function renumberScenes(scenes) {
  if (!Array.isArray(scenes)) return [];
  return scenes.map((s, i) => ({
    ...s,
    id: `scene-${i + 1}`,
  }));
}

/**
 * Default length for a newly added scene (model clip max, snapped).
 * @param {object} form
 */
export function defaultSceneDuration(form) {
  const limit = getClipLimitForModel(form?.model, clipOpts(form));
  const preferred = limit.maxSec || limit.allowed?.[limit.allowed.length - 1] || 8;
  return snapClipDuration(preferred, limit);
}

/**
 * Seed per-scene prompts from the shared brief / dialogue block.
 * @param {string} prompt
 * @param {number[]} durations
 * @returns {{ id: string; duration: number; prompt: string }[]}
 */
export function seedScenesFromPrompt(prompt, durations) {
  const list = durations?.length ? durations : [8];
  const chunks = splitPromptAcrossSegments(prompt, list.length, list);
  return list.map((duration, i) => ({
    id: `scene-${i + 1}`,
    duration,
    prompt: String(chunks[i] || "").trim(),
    imageUrlUploads: [],
  }));
}

/**
 * @-tags must NOT auto-split into scenes. Keep multi-speaker dialogue in one clip:
 * snap overlong durations to the model max and clear any stale auto scene plan.
 *
 * @param {object} form
 * @returns {object}
 */
export function normalizeFormForMultiSpeakerDialogue(form) {
  if (!form) return form;
  const lines = extractDialogueLinesFromPrompt(form.prompt);
  if (lines.length < 2) {
    if (form.multiSpeakerDialogue) {
      return { ...form, multiSpeakerDialogue: false };
    }
    return form;
  }

  const limit = getClipLimitForModel(form.model, clipOpts(form));
  let duration = Math.max(1, Math.round(Number(form.duration) || 0));
  let changed = false;

  // Prefer a single generation over stitching when @-tagged dialogue is present.
  if (limit.maxSec != null && duration > limit.maxSec) {
    duration = snapClipDuration(limit.maxSec, limit);
    changed = true;
  }

  const hadAutoScenes =
    form.multiSpeakerDialogue === true ||
    (Array.isArray(form.scenes) &&
      form.scenes.length > 1 &&
      form.scenes.every((s) => String(s?.speaker || "").trim()));

  if (hadAutoScenes || (Array.isArray(form.scenes) && form.scenes.length > 1 && changed)) {
    changed = true;
  }

  if (
    !changed &&
    !form.multiSpeakerDialogue &&
    !(Array.isArray(form.scenes) && form.scenes.length > 1)
  ) {
    return form;
  }

  return {
    ...form,
    duration: clampDuration(duration),
    scenes: null,
    multiSpeakerDialogue: false,
  };
}

/** @deprecated No longer used — @-tags do not split scenes. */
export function applyMultiSpeakerDialoguePlan(form) {
  return normalizeFormForMultiSpeakerDialogue(form);
}

/**
 * Append one scene and bump total duration (capped at MAX_DURATION_SEC).
 * @param {object} form
 * @returns {object | null} next form patch, or null if no room
 */
export function addSceneToForm(form) {
  const limit = getClipLimitForModel(form?.model, clipOpts(form));
  const existing = Array.isArray(form.scenes) ? [...form.scenes] : [];
  const currentTotal = totalScenesDuration(existing) || clampDuration(form.duration);
  const remaining = MAX_DURATION_SEC - currentTotal;
  if (remaining < limit.minSec) return null;

  let duration = defaultSceneDuration(form);
  if (duration > remaining) {
    if (limit.allowed?.length) {
      const fit = [...limit.allowed]
        .filter((a) => a <= remaining)
        .sort((a, b) => b - a)[0];
      if (fit == null || fit < limit.minSec) return null;
      duration = fit;
    } else {
      duration = Math.min(duration, remaining);
      duration = snapClipDuration(duration, limit);
      if (duration > remaining) {
        duration = Math.max(limit.minSec, remaining);
      }
    }
  }
  if (duration < limit.minSec || currentTotal + duration > MAX_DURATION_SEC) {
    return null;
  }

  const nextScenes = renumberScenes([
    ...existing,
    {
      id: `scene-${existing.length + 1}`,
      duration,
      prompt: "",
      imageUrlUploads: [],
    },
  ]);
  return {
    ...form,
    scenes: nextScenes,
    duration: clampDuration(totalScenesDuration(nextScenes)),
  };
}

/**
 * Reorder scenes by moving fromIndex → toIndex, then renumber 1…N.
 * Duration is unchanged; only playback/generation order updates.
 * @param {object} form
 * @param {number} fromIndex
 * @param {number} toIndex
 * @returns {object}
 */
export function reorderScenesInForm(form, fromIndex, toIndex) {
  const existing = Array.isArray(form.scenes) ? [...form.scenes] : [];
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= existing.length ||
    toIndex >= existing.length ||
    fromIndex === toIndex
  ) {
    return form;
  }
  const [moved] = existing.splice(fromIndex, 1);
  existing.splice(toIndex, 0, moved);
  return {
    ...form,
    scenes: renumberScenes(existing),
  };
}

/**
 * Set one scene's clip length; snap to model limits and bump form.duration to the new sum.
 * @param {object} form
 * @param {number} index
 * @param {number} rawDuration
 * @returns {object}
 */
export function setSceneDurationInForm(form, index, rawDuration) {
  const existing = Array.isArray(form.scenes) ? [...form.scenes] : [];
  if (index < 0 || index >= existing.length) return form;

  const limit = getClipLimitForModel(form.model, clipOpts(form));
  const others = totalScenesDuration(existing.filter((_, i) => i !== index));
  const budget = Math.max(0, MAX_DURATION_SEC - others);
  const maxSec = Math.min(limit.maxSec ?? MAX_DURATION_SEC, budget);
  if (maxSec < limit.minSec) return form;

  const allowed =
    limit.allowed?.filter((a) => a >= limit.minSec && a <= maxSec) || null;
  if (allowed?.length === 0) return form;

  const nextDuration = snapClipDuration(rawDuration, {
    ...limit,
    maxSec,
    allowed,
  });
  if (Number(existing[index].duration) === nextDuration) return form;

  existing[index] = { ...existing[index], duration: nextDuration };
  const nextScenes = renumberScenes(existing);
  const total = Math.min(
    MAX_DURATION_SEC,
    Math.max(1, Math.round(totalScenesDuration(nextScenes))),
  );
  return {
    ...form,
    scenes: nextScenes,
    duration: total,
  };
}

/**
 * Step a scene duration by ±1 allowed step (or 1s for continuous models).
 * @param {object} form
 * @param {number} index
 * @param {1|-1} direction
 * @returns {object}
 */
export function stepSceneDurationInForm(form, index, direction) {
  const existing = Array.isArray(form.scenes) ? form.scenes : [];
  if (index < 0 || index >= existing.length) return form;

  const limit = getClipLimitForModel(form.model, clipOpts(form));
  const others = totalScenesDuration(existing.filter((_, i) => i !== index));
  const budget = Math.max(0, MAX_DURATION_SEC - others);
  const maxSec = Math.min(limit.maxSec ?? MAX_DURATION_SEC, budget);
  const current = Number(existing[index]?.duration) || limit.minSec;
  const dir = direction >= 0 ? 1 : -1;

  if (limit.allowed?.length) {
    const allowed = limit.allowed.filter((a) => a >= limit.minSec && a <= maxSec);
    if (!allowed.length) return form;
    const snapped = snapClipDuration(current, { ...limit, maxSec, allowed });
    let idx = allowed.indexOf(snapped);
    if (idx < 0) idx = 0;
    const next = allowed[Math.max(0, Math.min(allowed.length - 1, idx + dir))];
    return setSceneDurationInForm(form, index, next);
  }

  return setSceneDurationInForm(form, index, current + dir);
}

/**
 * Remove scene at index; renumber and sync total duration.
 * @param {object} form
 * @param {number} index
 * @returns {object}
 */
export function removeSceneFromForm(form, index) {
  const existing = Array.isArray(form.scenes) ? form.scenes : [];
  if (index < 0 || index >= existing.length) return form;

  const nextScenes = renumberScenes(existing.filter((_, i) => i !== index));
  const total = Math.min(
    MAX_DURATION_SEC,
    Math.max(1, Math.round(totalScenesDuration(nextScenes) || Number(form.duration) || 1)),
  );

  // Drop the scene plan when a single clip is enough again.
  if (!needsAutoStitch(form.model, total, clipOpts(form))) {
    if (nextScenes.length > 1) {
      return { ...form, scenes: nextScenes, duration: total };
    }
    return { ...form, scenes: null, duration: clampDuration(total) };
  }

  // Keep at least one scene card while still stitching.
  if (!nextScenes.length) {
    const durations = distributeSegmentDurations(total, form.model, clipOpts(form));
    return {
      ...form,
      scenes: seedScenesFromPrompt(form.prompt, durations),
      duration: clampDuration(durations.reduce((s, d) => s + d, 0)),
    };
  }

  return {
    ...form,
    scenes: nextScenes,
    duration: total,
  };
}

/**
 * When target duration exceeds the model clip max, build/update the scene list.
 * Preserves existing scene prompts and media; only seeds empty / new slots.
 * @param {object} form
 * @returns {object[] | null}
 */
export function reconcileScenes(form) {
  const modelId = form?.model;
  const opts = clipOpts(form);
  const existing = Array.isArray(form.scenes) ? form.scenes : null;

  // @-tagged multi-speaker dialogue must stay a single clip — never auto-split.
  const speakerLines = extractDialogueLinesFromPrompt(form?.prompt);
  if (speakerLines.length >= 2) {
    return null;
  }

  // Duration fits in one API clip. Keep a manual multi-scene plan only when
  // scene lengths still sum to the target (user added scenes on purpose).
  // Otherwise collapse stale plans left over from a longer slider value
  // (e.g. Grok 6s after previously planning 2×6s).
  if (!needsAutoStitch(modelId, form?.duration, opts)) {
    if (existing?.length > 1) {
      const target = Math.max(1, Math.round(Number(form?.duration) || 0));
      const total = totalScenesDuration(existing);
      const limit = getClipLimitForModel(modelId, opts);
      const allFit =
        limit.maxSec == null ||
        existing.every((s) => (Number(s?.duration) || 0) <= limit.maxSec);
      if (total === target && allFit) {
        return renumberScenes(existing);
      }
    }
    return null;
  }

  const durations = distributeSegmentDurations(form.duration, modelId, opts);

  if (!existing?.length) {
    return seedScenesFromPrompt(form.prompt, durations);
  }

  const target = Math.min(
    MAX_DURATION_SEC,
    Math.max(1, Math.round(Number(form.duration) || 0)),
  );
  const existingTotal = totalScenesDuration(existing);

  // Same scene count and total already matches the slider — keep prompts/media.
  if (existing.length === durations.length && existingTotal === target) {
    return renumberScenes(
      existing.map((s, i) => ({
        id: s?.id || `scene-${i + 1}`,
        duration: Number(s?.duration) || durations[i],
        prompt: String(s?.prompt ?? ""),
        imageUrlUploads: s?.imageUrlUploads || [],
      })),
    );
  }

  // Grow, shrink, or re-time scenes to match the new target duration.
  // Always use durations.length so reducing 5m → 1m drops leftover Clip board slots.
  const seeded = seedScenesFromPrompt(form.prompt, durations);
  return renumberScenes(
    durations.map((duration, i) => {
      const prev = existing[i];
      const prevPrompt = String(prev?.prompt ?? "").trim();
      return {
        id: prev?.id || `scene-${i + 1}`,
        duration,
        prompt: prevPrompt || seeded[i]?.prompt || "",
        imageUrlUploads: prev?.imageUrlUploads || [],
      };
    }),
  );
}

/** True when the form should show the multi-scene editor. */
export function formHasScenePlan(form) {
  if (Array.isArray(form?.scenes) && form.scenes.length > 1) return true;
  return needsAutoStitch(form?.model, form?.duration, clipOpts(form));
}

/**
 * Durations to generate for a stitched run (prefer explicit scenes).
 * Falls back to a fresh split of `form.duration` when the scene list is stale
 * (e.g. a leftover 3s scene while the slider is 35s).
 * @param {object} form
 * @returns {number[]}
 */
export function sceneDurationsFromForm(form) {
  const target = Math.max(
    0,
    Math.round(Number(form?.duration) || 0),
  );
  if (Array.isArray(form?.scenes) && form.scenes.length > 0) {
    const fromScenes = form.scenes
      .map((s) => Number(s.duration) || 0)
      .filter((d) => d > 0);
    const sum = fromScenes.reduce((a, b) => a + b, 0);
    // Use the scene plan only when it matches the target length (not a stale longer plan).
    if (
      fromScenes.length &&
      target > 0 &&
      sum === target
    ) {
      return fromScenes;
    }
  }
  return distributeSegmentDurations(form.duration, form.model, clipOpts(form));
}

/**
 * Billable seconds for credit estimates — the user's target length from the slider.
 * @param {object} form
 * @returns {number}
 */
export function billableDurationFromForm(form) {
  return Math.max(1, Math.round(Number(form?.duration) || 0));
}

/**
 * Shared production brief for continuity wrappers (strips Dialogue when present).
 * @param {string} prompt
 */
export function sharedBriefFromPrompt(prompt) {
  const { brief, hasDialogue } = extractDialogueFromPrompt(prompt);
  return hasDialogue ? brief : String(prompt || "").trim();
}
